/**
 * TeamSection Component - Displays team information with collapsible member lists
 */
import React, { useState, memo } from 'react';
import { Badge } from '../ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { Users, ChevronDown, ChevronRight } from 'lucide-react';
import { colors } from './constants';
import UserCard from './UserCard';

const TeamSection = memo(({ team, onUserClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const completionBadgeColor = team.completion_rate >= 80 
    ? '#22c55e' 
    : team.completion_rate >= 50 
      ? '#f59e0b' 
      : '#ef4444';
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="rounded-lg border bg-white" style={{ borderColor: colors.border }}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5" style={{ color: colors.blue }} />
              <div>
                <p className="font-semibold" style={{ color: colors.dark }}>{team.team}</p>
                <p className="text-sm" style={{ color: colors.gray }}>
                  {team.total} Einträge
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right text-sm font-medium">
                <p className="text-green-600">{team.completed} gültig</p>
                <p className="text-red-600">{team.expired || 0} abgelaufen</p>
                <p className="text-orange-600">{team.not_completed} offen</p>
              </div>
              <Badge style={{ background: completionBadgeColor }}>
                {team.completion_rate}%
              </Badge>
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 border-t" style={{ borderColor: colors.border }}>
            {/* Expired Members */}
            {team.members_expired?.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium mb-2 text-red-600">
                  Abgelaufen ({team.members_expired.length})
                </p>
                <div className="space-y-1">
                  {team.members_expired.slice(0, 10).map((user, idx) => (
                    <UserCard key={idx} user={user} status="expired" onUserClick={onUserClick} />
                  ))}
                  {team.members_expired.length > 10 && (
                    <p className="text-xs text-center py-2" style={{ color: colors.gray }}>
                      ... und {team.members_expired.length - 10} weitere
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Not Completed Members */}
            {team.members_not_completed?.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium mb-2 text-orange-600">
                  Offen ({team.members_not_completed.length})
                </p>
                <div className="space-y-1">
                  {team.members_not_completed.slice(0, 10).map((user, idx) => (
                    <UserCard key={idx} user={user} status="not_completed" onUserClick={onUserClick} />
                  ))}
                  {team.members_not_completed.length > 10 && (
                    <p className="text-xs text-center py-2" style={{ color: colors.gray }}>
                      ... und {team.members_not_completed.length - 10} weitere
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Completed Members */}
            {team.members_completed?.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium mb-2 text-green-600">
                  Gültig ({team.members_completed.length})
                </p>
                <div className="space-y-1">
                  {team.members_completed.slice(0, 5).map((user, idx) => (
                    <UserCard key={idx} user={user} status="completed" onUserClick={onUserClick} />
                  ))}
                  {team.members_completed.length > 5 && (
                    <p className="text-xs text-center py-2" style={{ color: colors.gray }}>
                      ... und {team.members_completed.length - 5} weitere
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
});

TeamSection.displayName = 'TeamSection';

export default TeamSection;
